import React, { ChangeEvent, memo, ReactNode, SyntheticEvent, useEffect, useState } from "react"
import { TBody } from "./structures/TBody"
import { TBodyCell } from "./structures/TBodyCell"
import { TBodyRow } from "./components/TBodyRow"
import { TBodySkeleton } from "./structures/TBodySkeleton"
import { THeadCell } from "./structures/THeadCell"
import { THeadRow } from "./structures/THeadRow"
import { ColumnDescriptor } from "./types/ColumnDescriptor"
import { Profile, ProfileProps, ShortPatientProfile, ShortPatientProfileProps } from "../../molecules/profile"
import { TWrapperStickyScrollbar } from "./structures/TWrapperStickyScrollbar"
import styled from "styled-components"
import { TableTextProps } from "./types/TableTextProps"
import { Activity, ActivityProps } from "../../molecules/activity"
import { TableCustomContent } from "./types/TableCustomContent"
import { ProgressBadge, ProgressBadgeProps } from "../../atoms/progress-badge"
import { AvatarGroup, AvatarGroupProps } from "../../molecules/AvatarGroup"
import { CellProps, RowProps } from "./types/RowProps"
import { MultiLines, MultiLinesProps } from "../../molecules/multi-lines"
import { TableColumnType } from "./types/TableColumnType"
import { Line } from "../../atoms/line"
import { TextDisclaimer, TextSmall } from "../../atoms/text"
import { theme } from "../../../theme"
import { Checkbox } from "../../atoms/controls"
import { TPlaceholder } from "./components/TPlaceholder"
import { Pagination } from "./components/Pagination"
import { PaginationConfig } from "./types/config/PaginationConfig"
import { SearchConfig } from "./types/config/SearchConfig"
import { SelectionConfig } from "./types/config/SelectionConfig"
import { InfiniteScrollConfig } from "./types/config/InfiniteScrollConfig"
import { TableHeader } from "./components/TableHeader"
import { useTableSorter } from "./hooks/useTableSorter"
import { TableSorterQuery } from "./types/TableSorterQuery"
import { DropdownFilterState, FiltersConfig, SwitchFilterState, ToggleFilterState } from "./types/config/FiltersConfig"
import { CallToActionConfig } from "./types/config/CallToActionConfig"
import { TWrapper } from "./structures/TWrapper"
import { CircularProgress, TableHead } from "@mui/material"
import { ProgressBarMini, ProgressBarMiniProps } from "../../molecules/progress-bar-mini"
import { PlaceholderConfig } from "./types/config/PlaceholderConfig"

const MINIMAL_SEARCH_LENGTH = 3
const MINIMAL_SEARCH_DELAY = 300

export type SearchParameters = {
  searchText?: string
  rowStart?: number
  rowEnd?: number
  sortedColumn?: TableSorterQuery
  toggleFilters?: ToggleFilterState[]
  dropdownFilters?: DropdownFilterState[]
  switchFilters?: SwitchFilterState[]
}

export enum TableVariant {
  fullScreen = "fullScreen",
  asContent = "asContent",
  withBorders = "withBorders",
}

export type TableProps = {
  tableVariant: TableVariant
  tableTitle?: string
  isLoading: boolean
  forEachRowOnClick?: (row: RowProps, e?: SyntheticEvent<HTMLTableRowElement>) => void
  columns: ColumnDescriptor[]
  initialSort?: TableSorterQuery
  defaultSort?: TableSorterQuery
  rows: RowProps[]
  selectionConfig?: SelectionConfig
  searchConfig?: SearchConfig
  paginationConfig?: PaginationConfig
  infiniteScrollConfig?: InfiniteScrollConfig
  filtersConfig?: FiltersConfig
  callToActionConfig?: CallToActionConfig
  infoBox?: ReactNode
  hasRowBorder?: boolean
  onSearchParametersChange?: (searchParameters: SearchParameters) => void
  noEntryYetPlaceholder: PlaceholderConfig
  noResultsPlaceholder: PlaceholderConfig
}

export const Table = memo((props: TableProps) => {
  const {
    tableVariant,
    tableTitle,
    selectionConfig,
    searchConfig,
    paginationConfig,
    infiniteScrollConfig,
    filtersConfig,
    callToActionConfig,
    infoBox,
    onSearchParametersChange,
    isLoading,
    columns,
    initialSort,
    defaultSort,
    rows,
    forEachRowOnClick,
    hasRowBorder,
    noEntryYetPlaceholder,
    noResultsPlaceholder,
  } = props
  const [parentCheckboxChecked, setParentCheckboxChecked] = useState(false)
  const [parentCheckboxIndeterminate, setParentCheckboxIndeterminate] = useState(false)
  const [selectedRowsUniqueIdentifiers, setSelectedRowsUniqueIdentifiers] = useState<string[]>([])
  const [rowsPerPage, setRowsPerPage] = useState(
    paginationConfig?.initialRowStart ?? paginationConfig?.rowsPerPageOptions[0] ?? 10
  )
  const [rowStart, setRowStart] = useState(paginationConfig?.initialRowStart ?? 1)
  const [rowEnd, setRowEnd] = useState(
    paginationConfig?.totalRows ? Math.min(rowsPerPage, paginationConfig?.totalRows) : rowsPerPage
  )

  const [internalFiltersConfig, setInternalFiltersConfig] = useState<FiltersConfig>()

  const [isSearchFieldExpanded, setIsSearchFieldExpanded] = useState(false)
  const [searchValue, setSearchValue] = useState(searchConfig?.initialSearch ?? "")
  const isFullScreen = tableVariant === TableVariant.fullScreen
  const sampleRowActions = rows[0]?.actions ?? []

  const activeFilters =
    internalFiltersConfig?.toggleFilters &&
    internalFiltersConfig?.dropdownFilters &&
    internalFiltersConfig?.switchFilters
      ? internalFiltersConfig.toggleFilters.some((toggleFilter) => {
          return toggleFilter.chipProps.active === true
        }) ||
        internalFiltersConfig.dropdownFilters.some((dropdownFilter) => {
          return dropdownFilter.chipDropdownProps.value?.key !== dropdownFilter.resetState?.key
        }) ||
        internalFiltersConfig.switchFilters.some((switchFilter) => {
          return switchFilter.switchProps.value !== switchFilter.switchProps.defaultValue
        })
      : false

  const searchActive = searchValue.length > 0 || activeFilters

  const hasNoEntryYet = !isLoading && rows.length === 0 && !searchActive
  const hasNoResults = !isLoading && rows.length === 0 && searchActive

  const deepCopyOfFiltersConfig = () => {
    return filtersConfig ? (JSON.parse(JSON.stringify(filtersConfig)) as FiltersConfig) : undefined
  }

  useEffect(() => {
    setInternalFiltersConfig(deepCopyOfFiltersConfig())
  }, [])

  useEffect(() => {
    if (isLoading) {
      return
    }

    setSelectedRowsUniqueIdentifiers((selectedRows) => {
      const copy = selectedRows.slice()

      const filteredCopy = copy.filter((selectedRow) => {
        return rows.some((row) => {
          return row.uniqueIdentifier === selectedRow
        })
      })

      if (copy.length !== filteredCopy.length) {
        updateParentCheckboxState(filteredCopy)
        !!selectionConfig?.onSelectionChange && selectionConfig.onSelectionChange(filteredCopy)
      }

      return filteredCopy
    })
  }, [rows])

  const renderCell = (columnType: TableColumnType, cell?: CellProps) => {
    if (cell === undefined) {
      return
    }
    switch (columnType) {
      case TableColumnType.Text:
        return renderText(cell as TableTextProps)
      case TableColumnType.ShortPatientProfile:
        return <ShortPatientProfile {...(cell as ShortPatientProfileProps)}> </ShortPatientProfile>
      case TableColumnType.Activity:
        return <Activity {...(cell as ActivityProps)} />
      case TableColumnType.Profile:
        return <Profile {...(cell as ProfileProps)} />
      case TableColumnType.ProgressBadge:
        return <ProgressBadge {...(cell as ProgressBadgeProps)} />
      case TableColumnType.ProgressBarMini:
        return <ProgressBarMini {...(cell as ProgressBarMiniProps)} />
      case TableColumnType.AvatarGroup:
        return (
          <AvatarGroupWrapper>
            <AvatarGroup {...(cell as AvatarGroupProps)} />
          </AvatarGroupWrapper>
        )
      case TableColumnType.MultiLines:
        return <MultiLines {...(cell as MultiLinesProps)} />
      case TableColumnType.Custom:
        return <>{(cell as TableCustomContent).customCell}</>
      default:
        return
    }
  }

  const renderText = (cellContent: TableTextProps) => {
    const { onClickRow, ...lineProps } = cellContent
    return (
      <>
        {onClickRow && <HiddenLink onClick={() => onClickRow()}></HiddenLink>}
        <Line {...lineProps}></Line>
      </>
    )
  }

  const handleClickParentCheckbox = (event: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
    const checked = parentCheckboxIndeterminate ? false : isChecked

    setParentCheckboxIndeterminate(false)
    setParentCheckboxChecked(checked)

    const newSelectedRowsUniqueIdentifiers = checked
      ? rows
          .map((row) => row.uniqueIdentifier)
          .reduce((accumulator: string[], currentValue) => accumulator.concat(currentValue), [])
      : []

    setSelectedRowsUniqueIdentifiers(newSelectedRowsUniqueIdentifiers)

    !!selectionConfig?.onSelectionChange && selectionConfig.onSelectionChange(newSelectedRowsUniqueIdentifiers)
  }

  const allRowsAreSelected = (rowsIdentifiers: string[], allRows: RowProps[]) => {
    return allRows.every((row) => rowsIdentifiers.includes(row.uniqueIdentifier))
  }

  const noRowsAreSelected = (rowsIdentifiers: string[], allRows: RowProps[]) => {
    return allRows.every((row) => !rowsIdentifiers.includes(row.uniqueIdentifier))
  }

  const handleClickRowCheckbox = (isChecked: boolean, rowIdentifier: string) => {
    let newSelectedRowsUniqueIdentifiers = [...selectedRowsUniqueIdentifiers]

    if (isChecked) {
      newSelectedRowsUniqueIdentifiers.push(rowIdentifier)
    } else {
      newSelectedRowsUniqueIdentifiers = newSelectedRowsUniqueIdentifiers.filter((i) => {
        return i !== rowIdentifier
      })
    }

    setSelectedRowsUniqueIdentifiers(newSelectedRowsUniqueIdentifiers)
    updateParentCheckboxState(newSelectedRowsUniqueIdentifiers)
  }

  const updateParentCheckboxState = (newSelectedRowsUniqueIdentifiers: string[]) => {
    if (allRowsAreSelected(newSelectedRowsUniqueIdentifiers, rows)) {
      setParentCheckboxIndeterminate(false)
      setParentCheckboxChecked(true)
    } else if (noRowsAreSelected(newSelectedRowsUniqueIdentifiers, rows)) {
      setParentCheckboxIndeterminate(false)
      setParentCheckboxChecked(false)
    } else {
      setParentCheckboxIndeterminate(true)
      setParentCheckboxChecked(false)
    }

    !!selectionConfig?.onSelectionChange && selectionConfig.onSelectionChange(newSelectedRowsUniqueIdentifiers)
  }

  const dropdownFilterOutput = (specifiedInternalFiltersConfig?: FiltersConfig) => {
    const filterConfig = specifiedInternalFiltersConfig ?? internalFiltersConfig

    if (!!filterConfig?.dropdownFilters) {
      return filterConfig.dropdownFilters.map((dropdownFilter) => {
        return {
          technicalName: dropdownFilter.technicalName,
          selectedOption: dropdownFilter.chipDropdownProps.value,
        } as DropdownFilterState
      })
    }
  }

  const toggleFilterOutput = (specifiedInternalFiltersConfig?: FiltersConfig) => {
    const filterConfig = specifiedInternalFiltersConfig ?? internalFiltersConfig

    if (!!filterConfig?.toggleFilters) {
      return filterConfig.toggleFilters.map((toggleFilter) => {
        return {
          technicalName: toggleFilter.technicalName,
          active: toggleFilter.chipProps.active,
        } as ToggleFilterState
      })
    }
  }

  const switchFilterOutput = (specifiedInternalFiltersConfig?: FiltersConfig) => {
    const filterConfig = specifiedInternalFiltersConfig ?? internalFiltersConfig

    if (!!filterConfig?.switchFilters) {
      return filterConfig.switchFilters.map((switchFilter) => {
        return {
          technicalName: switchFilter.technicalName,
          selectedOption: switchFilter.switchProps.value,
        } as SwitchFilterState
      })
    }
  }

  const handleFilterChange = (newInternalFiltersConfig: FiltersConfig) => {
    setInternalFiltersConfig(newInternalFiltersConfig)
    triggerSearchParametersChange(searchValue, undefined, undefined, undefined, newInternalFiltersConfig)
  }

  const getSortedColumn = (specifiedSortedColumn?: TableSorterQuery) => {
    if (!specifiedSortedColumn) {
      return sorter.query
    }
    return specifiedSortedColumn?.columnName !== "" ? specifiedSortedColumn : undefined
  }

  // Specified is used when using state would take the old value
  const triggerSearchParametersChange = (
    specifiedSearchText?: string,
    specifiedRowStart?: number,
    specifiedRowEnd?: number,
    specifiedSortedColumn?: TableSorterQuery,
    specifiedInternalFiltersConfig?: FiltersConfig
  ) => {
    onSearchParametersChange &&
      onSearchParametersChange({
        searchText: specifiedSearchText ?? searchValue,
        rowStart: specifiedRowStart ?? rowStart,
        rowEnd: specifiedRowEnd ?? rowEnd,
        sortedColumn: getSortedColumn(specifiedSortedColumn),
        dropdownFilters: dropdownFilterOutput(specifiedInternalFiltersConfig),
        toggleFilters: toggleFilterOutput(specifiedInternalFiltersConfig),
        switchFilters: switchFilterOutput(specifiedInternalFiltersConfig),
      })
  }

  const onColumnSortChange = (newSorterQuery?: TableSorterQuery) => {
    triggerSearchParametersChange(undefined, undefined, undefined, newSorterQuery)
  }
  const sorter = useTableSorter(initialSort ?? undefined, defaultSort ?? undefined, onColumnSortChange)

  const onPaginationChange = (specifiedRowStart?: number, specifiedRowEnd?: number) => {
    triggerSearchParametersChange(undefined, specifiedRowStart, specifiedRowEnd)
  }

  const renderPagination = () => {
    return (
      paginationConfig &&
      !isFullScreen && (
        <Pagination
          {...paginationConfig}
          rowStart={rowStart}
          setRowStart={setRowStart}
          rowEnd={rowEnd}
          setRowEnd={setRowEnd}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          onChange={onPaginationChange}
        />
      )
    )
  }

  const renderTable = () => {
    return (
      <>
        <TableHead>
          <THeadRow>
            {!!selectionConfig && (
              <THeadCell>
                <Checkbox
                  dataCy={"parentCheckbox"}
                  onChange={handleClickParentCheckbox}
                  indeterminate={parentCheckboxIndeterminate}
                  checked={parentCheckboxChecked}
                />
              </THeadCell>
            )}
            {columns.map((column, index) => {
              return column.sortable ? (
                <THeadCell key={`thead-${index}`} {...sorter.bind(column.technicalName)} dataCy={column.dataCy}>
                  {column.title}
                </THeadCell>
              ) : (
                <THeadCell key={`thead-${index}`} dataCy={column.dataCy}>
                  {column.title}
                </THeadCell>
              )
            })}
            {sampleRowActions.length !== 0 && <THeadCell />}
          </THeadRow>
        </TableHead>

        {isLoading && <TBodySkeleton count={paginationConfig ? rowsPerPage : 10} colSpan={columns.length} />}

        {!isLoading && (
          <TBody>
            {rows.map((row, rowIndex) => {
              return (
                <TBodyRow
                  key={`row-${rowIndex}`}
                  onClick={(e) => {
                    forEachRowOnClick && forEachRowOnClick(row, e)
                    row.onClick && row.onClick()
                  }}
                  haveRowClickableChildren={row.haveARowClickableCell}
                  row={row}
                  isLastRow={rowIndex === rows.length - 1}
                  onRowVisible={infiniteScrollConfig?.onLastRowVisible}
                  checked={selectedRowsUniqueIdentifiers.includes(row.uniqueIdentifier)}
                  hasRowBorder={hasRowBorder}
                >
                  {!!selectionConfig && (
                    <CustomTBodyCell $width="40px">
                      <Checkbox
                        dataCy={`${row.uniqueIdentifier}-CheckBox`}
                        checked={selectedRowsUniqueIdentifiers.includes(row.uniqueIdentifier)}
                        onChange={(_, checked) => {
                          handleClickRowCheckbox(checked, row.uniqueIdentifier)
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </CustomTBodyCell>
                  )}
                  {columns.map((column, columnIndex) => {
                    return (
                      <CustomTBodyCell
                        key={`cell-${columnIndex}-${rowIndex}`}
                        dataCy={`cell-${row.cells[columnIndex]?.dataCy}`}
                        $maxWidth={column.maxWidth}
                        $minWidth={column.minWidth}
                        $width={column.widthPercent}
                      >
                        {renderCell(column.type, row.cells[columnIndex])}
                      </CustomTBodyCell>
                    )
                  })}
                </TBodyRow>
              )
            })}
          </TBody>
        )}

        {isFullScreen && !!infiniteScrollConfig?.isLoadingMore && (
          <TBody>
            <tr>
              <td colSpan={columns.length}>
                <LoadingMoreContainer>
                  <CustomCircularProgress size={22} />
                  <TextDisclaimer>{infiniteScrollConfig?.loadingMoreText}</TextDisclaimer>
                </LoadingMoreContainer>
              </td>
            </tr>
          </TBody>
        )}

        {renderPagination()}
      </>
    )
  }

  const renderTableOrTablePlaceholders = () => {
    // No entry yet placeholder when full screen
    if (hasNoEntryYet) {
      return (
        <ListPlaceholder
          title={noEntryYetPlaceholder.title}
          icon={noEntryYetPlaceholder.icon}
          color={theme.color.grey1}
        >
          {noEntryYetPlaceholder.content}
        </ListPlaceholder>
      )
    }
    // No results placeholder when full screen
    else if (hasNoResults) {
      return (
        <ListPlaceholder title={noResultsPlaceholder.title} icon={noResultsPlaceholder.icon} color={theme.color.grey1}>
          {noResultsPlaceholder.content}
        </ListPlaceholder>
      )
    }
    // In every other cases, render the table
    else {
      // When it is full screen we want to have a sticky horizontal scroll bar
      if (isFullScreen) {
        return <TWrapperStickyScrollbar>{renderTable()}</TWrapperStickyScrollbar>
      } else {
        return <TWrapper>{renderTable()}</TWrapper>
      }
    }
  }

  return (
    <Root $withBorders={tableVariant === TableVariant.withBorders}>
      <TableHeader
        isFullScreen={isFullScreen}
        tableTitle={tableTitle}
        headerSelectionConfig={
          selectionConfig
            ? { ...selectionConfig, selectedRowsUniqueIdentifiers: selectedRowsUniqueIdentifiers }
            : undefined
        }
        headerSearchConfig={
          searchConfig
            ? {
                ...searchConfig,
                onSearchChange: triggerSearchParametersChange,
                searchValue: searchValue,
                setSearchValue: setSearchValue,
                isSearchFieldExpanded: isSearchFieldExpanded,
                setIsSearchFieldExpanded: setIsSearchFieldExpanded,
              }
            : undefined
        }
        headerFiltersConfig={
          internalFiltersConfig
            ? {
                ...internalFiltersConfig,
                handleFilterChange: handleFilterChange,
              }
            : undefined
        }
        callToActionConfig={callToActionConfig}
        infoBox={infoBox}
        hasNoEntryYet={hasNoEntryYet}
      />
      {renderTableOrTablePlaceholders()}
    </Root>
  )
})

const Root = styled.div<{ $withBorders: boolean }>`
  height: 100%;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  border: ${(p) => {
    return p.$withBorders ? `1px solid ${theme.color.grey5}` : "unset"
  }};
`

const CustomTBodyCell = styled(TBodyCell)<{ $maxWidth?: string; $minWidth?: string; $width?: string }>`
  max-width: ${(p) => {
    return p.$maxWidth
  }};
  min-width: ${(p) => {
    return p.$minWidth
  }};
  width: ${(p) => {
    return p.$width
  }};
`

const HiddenLink = styled.a`
  &,
  &:hover,
  &:visited,
  &:active {
    text-decoration: none;
    outline: none;
  }

  &:after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    cursor: pointer;
  }
`

const CustomTextSmall = styled(TextSmall)`
  font-family: ${theme.font.regular};
`

const ListPlaceholder = styled(TPlaceholder)`
  display: flex;
  justify-content: center;
  max-width: 311px;
  margin-left: auto;
  margin-right: auto;

  svg {
    min-width: ${theme.buttonSize.m};
    min-height: ${theme.buttonSize.m};
    color: ${theme.color.grey2};
  }
`

const LoadingMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing.m};
  height: ${theme.spacing.xxl};
`

const CustomCircularProgress = styled(CircularProgress)`
  color: ${theme.color.grey2};
`

const AvatarGroupWrapper = styled.div`
  display: flex;
  flex-direction: row;
`
